import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Star, Crown, MapPin, Sparkles, Loader2, RefreshCw, X, Layers,
  Pin, Award, Filter, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { RARITIES, VARIANTS, RARITY_ORDER, VARIANT_ORDER, SPRITES } from '../mock';
import { POIS, SPAWN_TYPES, SPRITE_SPAWNS } from '../lib/mapData';
import { useIslandMap } from '../hooks/useIslandMap';
import { useUserPins } from '../hooks/useUserPins';
import { FilterGroup, FilterCheck } from './MapFilterControls';
import SpriteVaultMap from './SpriteVaultMap';

const RARITY_IDS = Object.keys(RARITY_ORDER); // ['rare','epic','legendary','mythic']

const POI_TYPES = [
  { id: 'named',    label: 'Named Locations' },
  { id: 'landmark', label: 'Landmarks' },
  { id: 'loot',     label: 'Loot Zones' },
];

const STATUS = [
  { id: 'owned',    label: 'Owned',    color: '#10b981' },
  { id: 'missing',  label: 'Missing',  color: '#94a3b8' },
  { id: 'mastered', label: 'Mastered', color: '#ffd900' },
];

export default function MapDialog({ open, onOpenChange, collection, getEntry, onTrade }) {
  const { mapUrl, loading, error, regenerate } = useIslandMap();
  const { pins, clearAll: clearUserPins } = useUserPins();
  const [sideOpen, setSideOpen] = useState(true);
  const [dropMode, setDropMode] = useState(false);

  // Filter state — start with everything enabled
  const [filters, setFilters] = useState(() => ({
    rarities: new Set(RARITY_IDS),
    variants: new Set(VARIANT_ORDER),
    status: new Set(['owned', 'missing', 'mastered']),
    spawnTypes: new Set(SPAWN_TYPES.map((s) => s.id)),
    poiTypes: new Set(POI_TYPES.map((p) => p.id)),
  }));

  const toggle = (key, value) => {
    setFilters((prev) => {
      const next = new Set(prev[key]);
      if (next.has(value)) next.delete(value); else next.add(value);
      return { ...prev, [key]: next };
    });
  };

  // Counts per rarity / variant for the side panel hint
  const counts = useMemo(() => {
    const r = {};
    const v = {};
    SPRITES.forEach((s) => {
      r[s.rarity] = (r[s.rarity] || 0) + 1;
      v[s.variant] = (v[s.variant] || 0) + 1;
    });
    const ownedCount = Object.values(collection || {}).filter((e) => e?.owned).length;
    const masteredCount = Object.values(collection || {}).filter((e) => e?.mastered).length;
    return { r, v, ownedCount, masteredCount, total: SPRITES.length };
  }, [collection]);

  // Spawn-type counts (deterministic — same for everyone)
  const spawnCounts = useMemo(() => {
    const acc = {};
    SPRITE_SPAWNS.forEach((sp) => { acc[sp.spawnType] = (acc[sp.spawnType] || 0) + 1; });
    return acc;
  }, []);

  // Reset filters on open close cycle so they don't leak across sessions in odd ways
  useEffect(() => {
    if (!open) {
      setDropMode(false);
    }
  }, [open]);

  const selectAll = () => setFilters({
    rarities: new Set(RARITY_IDS),
    variants: new Set(VARIANT_ORDER),
    status: new Set(['owned', 'missing', 'mastered']),
    spawnTypes: new Set(SPAWN_TYPES.map((s) => s.id)),
    poiTypes: new Set(POI_TYPES.map((p) => p.id)),
  });

  const clearAll = () => setFilters({
    rarities: new Set(),
    variants: new Set(),
    status: new Set(),
    spawnTypes: new Set(),
    poiTypes: new Set(),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="map-dialog"
        className="p-0 overflow-hidden border-[3px] border-[#3a1064] rounded-2xl shadow-[0_8px_0_#3a1064] !max-w-[100vw] w-screen h-screen sm:w-[96vw] sm:h-[94vh] sm:!max-w-[1600px]"
      >
        <DialogTitle className="sr-only">Sprite Vault Island Map</DialogTitle>
        <div className="flex flex-col h-full bg-[#fff7d6]">
          {/* Top bar */}
          <div className="flex items-center gap-2 px-4 py-3 bg-[#ffd900] border-b-[3px] border-[#3a1064] shrink-0">
            <button
              data-testid="toggle-side-menu-btn"
              onClick={() => setSideOpen((v) => !v)}
              className="h-9 w-9 rounded-lg bg-white border-2 border-[#3a1064] flex items-center justify-center shadow-[0_2px_0_#3a1064] text-[#3a1064]"
              aria-label="Toggle side menu"
            >
              {sideOpen ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
            <h2 className="font-black text-[#3a1064] text-lg sm:text-xl tracking-tight flex items-center gap-2 flex-1 min-w-0 truncate">
              <Sparkles className="h-5 w-5 text-[#ff2bd6]" /> Sprite Vault Island
            </h2>
            <Button
              data-testid="map-drop-pin-btn"
              onClick={() => setDropMode((v) => !v)}
              className={`h-9 rounded-lg border-2 border-[#3a1064] font-black text-xs shadow-[0_2px_0_#3a1064] px-3 ${
                dropMode ? 'bg-[#9d3dff] text-white' : 'bg-white text-[#3a1064] hover:bg-yellow-50'
              }`}
            >
              <Pin className="h-3.5 w-3.5 mr-1" /> {dropMode ? 'Click map…' : 'Drop pin'}
            </Button>
            <Button
              data-testid="map-regen-btn"
              variant="outline"
              onClick={regenerate}
              disabled={loading}
              className="h-9 rounded-lg border-2 border-[#3a1064] bg-white text-[#3a1064] font-black text-xs hidden sm:inline-flex"
              title="Regenerate map illustration"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> New map
            </Button>
            <button
              data-testid="map-close-btn"
              onClick={() => onOpenChange(false)}
              className="h-9 w-9 rounded-lg bg-white border-2 border-[#3a1064] flex items-center justify-center shadow-[0_2px_0_#3a1064] text-[#3a1064]"
              aria-label="Close map"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 flex min-h-0">
            {/* Side menu */}
            <aside
              data-testid="map-side-menu"
              className={`bg-white border-r-[3px] border-[#3a1064] overflow-y-auto transition-all duration-300 shrink-0 ${
                sideOpen ? 'w-[280px]' : 'w-0'
              }`}
            >
              <div className={`${sideOpen ? 'block' : 'hidden'}`}>
                <div className="px-4 py-3 bg-yellow-50 border-b-2 border-[#3a1064]/15 flex items-center gap-2 sticky top-0 z-10">
                  <Filter className="h-4 w-4 text-[#ff2bd6]" />
                  <span className="font-black text-[#3a1064] text-sm uppercase tracking-wider flex-1">Filters</span>
                  <button
                    data-testid="filters-all-btn"
                    onClick={selectAll}
                    className="text-[10px] font-black text-[#3a1064] underline underline-offset-2 hover:text-[#ff2bd6]"
                  >All</button>
                  <button
                    data-testid="filters-none-btn"
                    onClick={clearAll}
                    className="text-[10px] font-black text-[#3a1064] underline underline-offset-2 hover:text-[#ff2bd6]"
                  >None</button>
                </div>

                <FilterGroup icon={Crown} title="Rarity" testId="filter-rarity">
                  {RARITY_IDS.map((id) => {
                    const r = RARITIES[id];
                    return (
                      <FilterCheck
                        key={id}
                        testId={`filter-rarity-${id}`}
                        checked={filters.rarities.has(id)}
                        onChange={() => toggle('rarities', id)}
                        label={r.label}
                        color={r.color}
                        count={counts.r[id] || 0}
                      />
                    );
                  })}
                </FilterGroup>

                <FilterGroup icon={Sparkles} title="Variant" testId="filter-variant">
                  {VARIANT_ORDER.map((id) => {
                    const v = VARIANTS[id];
                    return (
                      <FilterCheck
                        key={id}
                        testId={`filter-variant-${id}`}
                        checked={filters.variants.has(id)}
                        onChange={() => toggle('variants', id)}
                        label={v.label}
                        color={v.color}
                        count={counts.v[id] || 0}
                      />
                    );
                  })}
                </FilterGroup>

                <FilterGroup icon={Award} title="Status" testId="filter-status">
                  {STATUS.map((s) => (
                    <FilterCheck
                      key={s.id}
                      testId={`filter-status-${s.id}`}
                      checked={filters.status.has(s.id)}
                      onChange={() => toggle('status', s.id)}
                      label={s.label}
                      color={s.color}
                      count={s.id === 'owned' ? counts.ownedCount : s.id === 'mastered' ? counts.masteredCount : counts.total - counts.ownedCount}
                    />
                  ))}
                </FilterGroup>

                <FilterGroup icon={MapPin} title="POI Categories" testId="filter-poi">
                  {POI_TYPES.map((p) => (
                    <FilterCheck
                      key={p.id}
                      testId={`filter-poi-${p.id}`}
                      checked={filters.poiTypes.has(p.id)}
                      onChange={() => toggle('poiTypes', p.id)}
                      label={p.label}
                      count={POIS.filter((x) => x.type === p.id).length}
                    />
                  ))}
                </FilterGroup>

                <FilterGroup icon={Layers} title="Spawn Type" testId="filter-spawn">
                  {SPAWN_TYPES.map((s) => (
                    <FilterCheck
                      key={s.id}
                      testId={`filter-spawn-${s.id}`}
                      checked={filters.spawnTypes.has(s.id)}
                      onChange={() => toggle('spawnTypes', s.id)}
                      label={s.label}
                      color={s.color}
                      count={spawnCounts[s.id] || 0}
                    />
                  ))}
                </FilterGroup>

                {pins.length > 0 && (
                  <FilterGroup icon={Pin} title={`Your pins (${pins.length})`} testId="filter-pins" defaultOpen={false}>
                    <button
                      data-testid="clear-user-pins-btn"
                      onClick={() => {
                        if (window.confirm('Remove all your pins?')) clearUserPins();
                      }}
                      className="text-xs font-black text-rose-600 hover:text-rose-800 underline underline-offset-2"
                    >
                      Clear all my pins
                    </button>
                  </FilterGroup>
                )}
              </div>
            </aside>

            {/* Map area */}
            <div className="flex-1 relative min-w-0 bg-[#0b0625]">
              {loading && !mapUrl ? (
                <div className="absolute inset-0 flex items-center justify-center text-white">
                  <div className="text-center">
                    <Loader2 className="h-12 w-12 mx-auto animate-spin text-[#ffd900]" />
                    <div className="mt-3 font-black text-lg">Generating Sprite Vault Island…</div>
                    <div className="text-sm font-bold opacity-80 mt-1">Gemini Nano Banana is painting your map. ~10-20s</div>
                  </div>
                </div>
              ) : error && !mapUrl ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center bg-white border-[3px] border-[#3a1064] rounded-2xl p-6 shadow-[0_4px_0_#3a1064] max-w-md mx-4">
                    <div className="font-black text-[#3a1064] text-lg">Map failed to load</div>
                    <div className="text-sm font-bold text-[#3a1064]/70 mt-1">{error}</div>
                    <Button
                      onClick={regenerate}
                      className="mt-3 h-10 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" /> Try again
                    </Button>
                  </div>
                </div>
              ) : (
                <SpriteVaultMap
                  mapUrl={mapUrl}
                  collection={collection}
                  getEntry={getEntry}
                  filters={filters}
                  dropMode={dropMode}
                  setDropMode={setDropMode}
                  onTrade={(sprite) => {
                    onOpenChange(false);
                    onTrade?.(sprite);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
